import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useId, type ReactNode } from 'react';

import { fmtNumber } from '../lib/format';

interface TablePaginationProps {
  page: number;
  pageCount: number;
  pageSize: number;
  initialPageSize: number;
  total: number;
  caption?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/** A bounded window, including the first and last page, even for large lists. */
function pageItems(page: number, count: number): (number | 'gap-start' | 'gap-end')[] {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index);
  const start = Math.max(1, Math.min(page - 1, count - 5));
  const end = Math.min(count - 2, Math.max(page + 1, 4));
  return [
    0,
    ...(start > 1 ? ['gap-start' as const] : []),
    ...Array.from({ length: end - start + 1 }, (_, index) => start + index),
    ...(end < count - 2 ? ['gap-end' as const] : []),
    count - 1,
  ];
}

export function TablePagination({
  page, pageCount, pageSize, initialPageSize, total, caption, onPageChange, onPageSizeChange,
}: TablePaginationProps): ReactNode {
  const id = useId();
  const { t } = useLingui();
  const first = total === 0 ? 0 : page * pageSize + 1;
  const last = Math.min((page + 1) * pageSize, total);
  const sizes = [...new Set([5, 10, 25, 50, 100, initialPageSize, pageSize])].sort((a, b) => a - b);

  return (
    <div className="data-table-pagination">
      <div className="data-table-pagination-summary">
        <span
          className="data-table-range"
          role="status"
          aria-live="polite"
          aria-atomic
        >
          <strong>{total === 0 ? '0' : `${fmtNumber(first)}–${fmtNumber(last)}`}</strong>
          {' '}
          <Plural value={total} one="von # Datensatz" other="von # Datensätzen" />
        </span>
        <label className="data-table-page-size" htmlFor={`${id}-size`}>
          <Trans>Zeilen pro Seite</Trans>
          <select id={`${id}-size`} value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
            {sizes.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
      </div>
      <nav className="data-table-pages" aria-label={caption ? t`Seitennavigation: ${caption}` : t`Tabellenseiten`}>
        <button type="button" className="data-table-page-button" disabled={page === 0}
          aria-label={t`Vorherige Seite`} onClick={() => onPageChange(page - 1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m14 6-6 6 6 6" />
          </svg>
        </button>
        <div className="data-table-page-numbers">
          {pageItems(page, pageCount).map((item) => typeof item === 'number' ? (
            <button key={item} type="button" className="data-table-page-button"
              aria-label={t`Seite ${item + 1}`} aria-current={item === page ? 'page' : undefined}
              onClick={() => onPageChange(item)}>{item + 1}</button>
          ) : <span key={item} className="data-table-page-gap" aria-hidden="true">…</span>)}
        </div>
        <label className="data-table-page-jump" htmlFor={`${id}-page`}>
          <span className="visually-hidden"><Trans>Seite wählen</Trans></span>
          <select id={`${id}-page`} value={page} onChange={(event) => onPageChange(Number(event.target.value))}>
            {Array.from({ length: pageCount }, (_, index) => (
              <option key={index} value={index}>{t`Seite ${index + 1} von ${pageCount}`}</option>
            ))}
          </select>
        </label>
        <button type="button" className="data-table-page-button" disabled={page >= pageCount - 1}
          aria-label={t`Nächste Seite`} onClick={() => onPageChange(page + 1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m10 6 6 6-6 6" />
          </svg>
        </button>
      </nav>
    </div>
  );
}
